import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { debounceTime, filter, switchMap, take, tap } from 'rxjs/operators';
import { NotificationCode } from '../models/notification-code.enum';
import { INotificationDetails } from '../models/notification-details.model';
import {
  CustomerBillerNotification,
  INotification,
  LiabilityAutomaticReadingNotification,
  LiabilityNotification,
  ModalCreatedNotification,
} from '../models/notification.model';
import { ApiService } from './api.service';
import { CustomerStoreService } from './customer-store.service';
import { ModalService } from './modal.service';
import {
  EMPTY_CART_NEW_BILLER_FOR_COUPON_PROCESS_PARAM,
  NEW_CUSTOMER_BILLER_ID_PARAM,
} from '../../shared/components/empty-cart/empty-cart.component';
import { Subject } from 'rxjs';
import { CustomerBiller } from '../../billers/models/model';
import { CustomerBillerStatus } from '../../billers/models/customer-biller-status.model';

const DEBOUNCE_MS = 1000;

@Injectable({
  providedIn: 'root',
})
export class InstantNotificationService {
  private emptyCartNewBillerForCouponProcess = false;
  public reloadUserpanelAccess = new Subject<INotificationDetails>();

  constructor(
    private api: ApiService,
    private snackbar: MatSnackBar,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private customerStoreService: CustomerStoreService,
    private modalService: ModalService,
  ) {
    this.subscribeCustomerBillerNotifications();
    this.subscribeLiabilityNotifications();
    this.subscribeIdentityVerificationNotifications();
    this.subscribeLiabilityAutomaticReadingNotifications();
    this.subscribeModalCreatedNotifications();

    this.activatedRoute.queryParamMap.subscribe(
      (queryParams) =>
        (this.emptyCartNewBillerForCouponProcess =
          queryParams.get(EMPTY_CART_NEW_BILLER_FOR_COUPON_PROCESS_PARAM) === 'true' ||
          !!queryParams.get(NEW_CUSTOMER_BILLER_ID_PARAM)),
    );
  }

  private static getLiabilityNotificationDetails(): INotificationDetails {
    return {
      action: 'ZOBACZ',
      description: 'Dodano nową opłatę',
      redirectUrl: 'liabilities/all',
    };
  }

  private static getLiabilityAutomaticReadingNotification(
    notification: LiabilityAutomaticReadingNotification,
  ): INotificationDetails {
    return {
      action: 'ZOBACZ',
      description: 'Odczytywanie opłaty zostało zakończone',
      redirectUrl: `liabilities/data-confirmation/${notification.liabilityAutomaticReadingId}`,
    };
  }

  private getCustomerBillerNotificationDetails(notification: CustomerBillerNotification): INotificationDetails {
    let redirectUrl: string = '/billers/' + notification.customerBillerId + '/details/USERPANEL_SCRAPER';
    if (notification.draftLiabilitySourceId) {
      redirectUrl += `?draftId=${notification.draftLiabilitySourceId}`;
    }

    const notificationsDetails = {
      [NotificationCode.USER_PANEL_ACCESS_INVALID]: {
        action: 'POPRAW',
        description: 'Nieprawidłowe dane do logowania',
        redirectUrl,
      },
      [NotificationCode.USER_PANEL_ACCESS_FOLLOW_UP_CREDENTIALS_REQUIRED]: {
        action: 'KONFIGURUJ',
        description: 'Wymagane podanie kodu OTP.',
        redirectUrl,
      },
      [NotificationCode.USER_PANEL_ACCESS_INVALID_FOLLOW_UP_CREDENTIALS]: {
        action: 'POPRAW',
        description: 'Nieprawidłowy kod OTP.',
        redirectUrl,
      },
      [NotificationCode.USER_PANEL_SOURCE_ENABLED]: {
        action: null,
        description: 'Połączenie z systemem dostawcy (eBOK) dla ' + notification.billerName + ' zostało włączone',
        redirectUrl: null,
      },
      [NotificationCode.EMAIL_SOURCE_ENABLED]: {
        action: null,
        description: 'Połączenie ze skrzynką e-mail dla ' + notification.billerName + ' zostało włączone',
        redirectUrl: null,
      },
    };

    return notificationsDetails[notification.notificationCode];
  }

  private subscribeCustomerBillerNotifications(): void {
    this.api
      .subscribe<CustomerBillerNotification>('notifications/customerBiller')
      .pipe(
        filter((notification) => !!notification && !this.emptyCartNewBillerForCouponProcess),
        debounceTime(DEBOUNCE_MS),
        tap((notification: CustomerBillerNotification) => {
          this.showSnackbar(this.getCustomerBillerNotificationDetails(notification));
          this.reloadUserpanelAccess.next(this.getCustomerBillerNotificationDetails(notification));
        }),
      )
      .subscribe();
    this.api
      .subscribe<CustomerBiller>('customerBiller')
      .pipe(
        filter((notification) => !!notification),
        debounceTime(DEBOUNCE_MS),
        tap((notification: CustomerBiller) => {
            var newValue: INotificationDetails;
            if (notification.status === CustomerBillerStatus.ENABLED ||
              notification.status === CustomerBillerStatus.CONFIRMED ||
              notification.status === CustomerBillerStatus.VERIFYING) {
              newValue = {
                title: 'Dostawca dodany poprawnie',
                action: 'Zamknij',
                description: 'Połączenie z systemem dostawcy (eBOK) dla ' + notification.biller.name + ' zostało włączone',
                redirectUrl: '/billers',
              };
            } else if (notification.status === CustomerBillerStatus.INVALID) {
              newValue = {
                title: 'Błąd',
                action: 'Popraw dane',
                description: 'Nieprawidłowe dane do logowania',
                redirectUrl: null,
              };
            } else if (notification.status === CustomerBillerStatus.FOLLOW_UP_CREDENTIALS_REQUIRED) {
              newValue = {
                title: 'Dodawanie dostawycy',
                action: 'Skonfiguruj',
                description: 'Wymagane podanie kodu OTP.',
                redirectUrl: null,
              };
            } else if (notification.status === CustomerBillerStatus.USER_ACTION_NEEDED) {
              newValue = {
                title: 'Dodawanie dostawycy',
                action: 'Dokończ',
                description: 'Wymagana akcja użytkownika.',
                redirectUrl: null,
              };
            } else if (notification.status === CustomerBillerStatus.MISSING_CONFIGURATION) {
              newValue = {
                title: 'Dodawanie dostawycy',
                action: 'Dokończ',
                description: 'Uzupełnij wymagane dane.',
                redirectUrl: null,
              };
            }
            this.reloadUserpanelAccess.next(newValue);
          },
        ),
      )
      .subscribe();
  }

  private subscribeLiabilityNotifications(): void {
    this.api
      .subscribe<LiabilityNotification>('notifications/liability')
      .pipe(
        filter((notification) => !!notification),
        debounceTime(DEBOUNCE_MS),
        tap(() => {
          this.showSnackbar(InstantNotificationService.getLiabilityNotificationDetails());
        }),
      )
      .subscribe();
  }

  private subscribeIdentityVerificationNotifications(): void {
    this.api
      .subscribe<INotification>('notifications/identityVerification')
      .pipe(
        filter((notification) => !!notification),
        debounceTime(DEBOUNCE_MS),
        tap((notification: INotification) => {
          const description =
            notification.notificationCode === NotificationCode.IDENTITY_VERIFIED
              ? 'Gratulacje! Twoja tożsamość została zweryfikowana, a konto zostało odblokowane'
              : 'Weryfikacja Twojego dokumentu tożsamości nie powiodła się';
          this.showSnackbar({ action: null, description, redirectUrl: '' });
        }),
        filter(() => !!this.customerStoreService.state?.customerId),
        switchMap(() => this.customerStoreService.loadCustomer(this.customerStoreService.state.customerId)),
      )
      .subscribe();
  }

  private subscribeLiabilityAutomaticReadingNotifications(): void {
    this.api
      .subscribe<LiabilityAutomaticReadingNotification>('notifications/liabilityAutomaticReading')
      .pipe(
        filter((notification) => !!notification),
        debounceTime(DEBOUNCE_MS),
        tap((notification: LiabilityAutomaticReadingNotification) => {
          const currentPath = this.router.routerState.snapshot.url;
          if (currentPath === `/liabilities/reading/${notification.liabilityAutomaticReadingId}`) {
            this.router.navigateByUrl(`liabilities/data-confirmation/${notification.liabilityAutomaticReadingId}`);
          } else {
            this.showSnackbar(InstantNotificationService.getLiabilityAutomaticReadingNotification(notification));
          }
        }),
      )
      .subscribe();
  }

  private subscribeModalCreatedNotifications(): void {
    this.api
      .subscribe<ModalCreatedNotification>('notifications/modalCreated')
      .pipe(
        filter((notification) => !!notification),
        debounceTime(DEBOUNCE_MS),
        take(1),
        tap(() => {
          this.modalService.checkAndShowModal();
        }),
      )
      .subscribe();
  }

  private showSnackbar(details: INotificationDetails): void {
    if (!details) {
      return;
    }
    // this.snackbar
    //   .open(details.description, details.action, { duration: DEFAULT_DURATION_MS })
    //   .afterDismissed()
    //   .pipe(
    //     filter((dismiss: MatSnackBarDismiss) => dismiss.dismissedByAction),
    //     switchMap(() => this.router.navigateByUrl(details.redirectUrl)),
    //   )
    //   .subscribe();
  }
}
